// /clients/permanentky — Modul 4: prehled aktivnich permanentkářů 10 OT (read-only).
// Sales/CSM dashboard — vidi vsech ~30 permanentkářů na jednom miste, status zone, kolik zbyva,
// kdy expiruje, datum posledni faktury. Klik na radek = ClientDetail modal.
//
// 9.9.2026 (fáze 4b): sjednoceno do vzorníku. Akce jsou ikony v hlavičce, KPI
// jedou přes `DlazdiceFiltr`, stav přes `Stitek` s tóny, filtry přes `.field`.
// Na telefonu se z osmi sloupců zobrazovaly tři, proto je tam místo tabulky
// seznam karet; na počítači tabulka zůstává beze změny sloupců. Data, filtry,
// řazení, prefetch na řádku ani otevírání detailu se nemění.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Award, AlertTriangle, CheckCircle2, Clock, RefreshCw,
  Users, Wallet, ArrowLeft, Search, ChevronRight,
} from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import { Stranka, DlazdiceFiltr, Stitek, EmptyState, SkeletonRows, Hlaska, AkceZnovu } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { useApiData } from "../lib/api.js";
import { prefetchClientData } from "../lib/prefetchClient.js";
import ClientDetail from "./ClientDetail.jsx";
import { formatDate as fmtDate } from "../lib/format.js";

function fmt(n) { return Number(n || 0).toLocaleString("cs-CZ"); }
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return fmtDate(d);
}
// Dlouhé názvy hal se v tabulce lámaly, zkratky jsou v provozu zavedené.
function lokalitaKratce(l) {
  if (!l) return "—";
  return l
    .replace("Praha - Kbely - VT GYM", "VT GYM")
    .replace("VT GYM - Kbely", "VT GYM")
    .replace("Sportovní Hala Kbely", "Sportovní hala")
    .replace("Kbely - Sportovní hala", "Sportovní hala");
}

function stavPermanentky(p) {
  if (p.konecPo) return { ton: "problem", ikona: AlertTriangle, text: "Po expiraci" };
  if (p.status === "expired") return { ton: "problem", ikona: AlertTriangle, text: "Vyčerpáno" };
  if (p.status === "alert") return { ton: "pozor", ikona: Clock, text: "Brzy dojde" };
  return { ton: "ok", ikona: CheckCircle2, text: "V pořádku" };
}

function StatusBadge({ status, konecPo, velikost = "mala" }) {
  const s = stavPermanentky({ status, konecPo });
  return <Stitek ton={s.ton} velikost={velikost} ikona={s.ikona}>{s.text}</Stitek>;
}

export default function Permanentky() {
  const navigate = useNavigate();
  const uzka = useUzkaObrazovka();
  const { data, loading, error, refetch, refreshing } = useApiData("/api/permanentky");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTrener, setFilterTrener] = useState("all");
  const [selected, setSelected] = useState(null);

  const permanentky = data?.permanentky || [];
  const kpi = data?.kpi || { total: 0, ok: 0, alert: 0, expired: 0, konecBrzy: 0, konecPo: 0, mesicniRevenue: 0 };

  const trainers = useMemo(() => {
    const set = new Set();
    permanentky.forEach((p) => { if (p.trener) set.add(p.trener); });
    return [...set].sort();
  }, [permanentky]);

  const filtered = useMemo(() => {
    const sLow = String(search || "").toLowerCase();
    return permanentky.filter((p) => {
      if (filterStatus === "expired" && p.status !== "expired") return false;
      if (filterStatus === "alert" && p.status !== "alert") return false;
      if (filterStatus === "ok" && p.status !== "ok") return false;
      if (filterStatus === "konecBrzy" && !p.konecBrzy && !p.konecPo) return false;
      if (filterTrener !== "all" && p.trener !== filterTrener) return false;
      if (sLow && !String(p.klient || "").toLowerCase().includes(sLow)) return false;
      return true;
    }).sort((a, b) => {
      // Primární: zbývající tréninky ascending (0 nahoře, 10 dole). NaN/missing → nakonec.
      const az = parseInt(a.stavTreninku, 10);
      const bz = parseInt(b.stavTreninku, 10);
      const aNum = isNaN(az) ? 999 : az;
      const bNum = isNaN(bz) ? 999 : bz;
      if (aNum !== bNum) return aNum - bNum;
      // Sekundární: konec spolupráce dříve nahoře
      const ad = a.konec ? new Date(a.konec).getTime() : Infinity;
      const bd = b.konec ? new Date(b.konec).getTime() : Infinity;
      return ad - bd;
    });
  }, [permanentky, search, filterStatus, filterTrener]);

  const filtrZapnuty = filterStatus !== "all" || filterTrener !== "all" || search.trim() !== "";

  return (
    <Stranka>
      <GradientHeader
        title="Permanentky 10 OT"
        icon={Award}
        loading={loading}
        akce={[
          { klic: "zpet", ikona: ArrowLeft, popis: "Zpět na klienty", onClick: () => navigate("/clients") },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
        ]}
      />

      {/* Tři dlaždice filtrují, čtvrtá je jen údaj. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <DlazdiceFiltr
          popisek="Aktivní permanentky" hodnota={kpi.total} ikona={Users} ton="znacka"
          info={<InfoTip text="Klienti se Stav=Aktivní + typSpoluprace=Permice nebo balíček obsahuje 'Permanentka'. Klik vyčistí filtr a zobrazí všechny." />}
          zapnuto={filterStatus === "all"} onClick={() => setFilterStatus("all")}
        />
        <DlazdiceFiltr
          popisek="Potřebují fakturu" hodnota={kpi.expired} ikona={AlertTriangle} ton="problem"
          info={<InfoTip text="Klienti s 0 nebo méně zbývajícími tréninky. Vystavte novou fakturu. Klik filtruje, druhý klik vrátí." />}
          zapnuto={filterStatus === "expired"} onClick={() => setFilterStatus(filterStatus === "expired" ? "all" : "expired")}
        />
        <DlazdiceFiltr
          popisek="Brzy dojde" hodnota={kpi.alert} ikona={Clock} ton="pozor"
          info={<InfoTip text="Klienti s 1-3 zbývajícími tréninky. Připomenout klientovi/trenérovi. Klik filtruje, druhý klik vrátí." />}
          zapnuto={filterStatus === "alert"} onClick={() => setFilterStatus(filterStatus === "alert" ? "all" : "alert")}
        />
        <DlazdiceFiltr
          popisek="Měsíční revenue" hodnota={`${fmt(Math.round(kpi.mesicniRevenue))} Kč`} ikona={Wallet} ton="ok"
          info={<InfoTip text="Součet auto-spočítaných castka/měsíc napříč všemi aktivními permanentkáři. FAPI total / multiplier dle frekvence tréninků." />}
        />
      </div>

      <Card
        title="Permanentkáři"
        ikona={Award}
        lista
        odznak={data ? `${filtered.length} z ${kpi.total}` : undefined}
      >
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(200px,1fr)_auto_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat klienta…" aria-label="Hledat klienta"
              className="field pl-9" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} aria-label="Stav permanentky"
            className={`field ${filterStatus !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="all">Všechny stavy</option>
            <option value="expired">Potřebují fakturu ({kpi.expired})</option>
            <option value="alert">Brzy dojde ({kpi.alert})</option>
            <option value="ok">V pořádku ({kpi.ok})</option>
            <option value="konecBrzy">Konec do 30 dní</option>
          </select>
          {trainers.length > 1 && (
            <select value={filterTrener} onChange={(e) => setFilterTrener(e.target.value)} aria-label="Trenér"
              className={`field ${filterTrener !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
              <option value="all">Všichni trenéři</option>
              {trainers.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
        </div>

        {error && <Hlaska ton="problem" title="Permanentky se nenačetly" akce={<AkceZnovu onClick={refetch} />}>{String(error)}</Hlaska>}

        {loading && permanentky.length === 0 ? (
          <SkeletonRows rows={6} />
        ) : !error && filtered.length === 0 ? (
          <EmptyState icon={Award} title="Žádní permanentkáři" hint={filtrZapnuty ? "Nic neodpovídá filtru." : "Zatím tu nikdo není."} />
        ) : uzka ? (
          /* Telefon: z osmi sloupců byly vidět tři, zbytek se schovával. Karta
             ukáže všechno podstatné: jméno, balíček, stav, zbývá, trenér, konec. */
          <div className="space-y-2">
            {filtered.map((p, i) => {
              const zbyva = parseInt(p.stavTreninku, 10);
              const zbyvaDisplay = isNaN(zbyva) ? "—" : zbyva;
              const s = stavPermanentky(p);
              return (
                <button
                  key={p.klient + i}
                  type="button"
                  onClick={() => setSelected(p)}
                  className={`polozka w-full text-left active:bg-brand-light ${
                    p.status === "expired" ? "border-rose-200 bg-rose-50/40" : p.status === "alert" ? "border-amber-200 bg-amber-50/40" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{p.klient}</div>
                      <div className="text-[12px] leading-snug text-slate-500">{p.balicek}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className={`text-xl font-black tabular-nums ${s.ton === "problem" ? "text-rose-600" : s.ton === "pozor" ? "text-amber-600" : "text-slate-900"}`}>{zbyvaDisplay}</span>
                      <span className="text-[12px] text-slate-400">/10</span>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={p.status} konecPo={p.konecPo} />
                    {p.trener && <Stitek ton="neutralni" velikost="mala">{p.trener}</Stitek>}
                    {p.lokalita && <Stitek ton="neutralni" velikost="mala">{lokalitaKratce(p.lokalita)}</Stitek>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-slate-500">
                    {p.konec && (
                      <span>
                        Konec {formatDate(p.konec)}
                        {p.daysUntilEnd !== null && (
                          <span className={p.konecPo ? " font-bold text-rose-600" : p.konecBrzy ? " font-bold text-amber-600" : ""}>
                            {" "}({p.konecPo ? `před ${-p.daysUntilEnd} dny` : `za ${p.daysUntilEnd} dní`})
                          </span>
                        )}
                      </span>
                    )}
                    {p.posledniTrenink && (
                      <span>
                        Poslední trénink {formatDate(p.posledniTrenink)}
                        {p.posledniTreninkDays !== null && (
                          <span className={p.posledniTreninkDays > 15 ? " font-bold text-rose-600" : p.posledniTreninkDays > 7 ? " font-bold text-amber-600" : ""}>
                            {" "}(před {p.posledniTreninkDays} dny)
                          </span>
                        )}
                      </span>
                    )}
                    {p.fapiLastInvoiceDate && (
                      <span>Poslední faktura {formatDate(p.fapiLastInvoiceDate)}{p.fapiLastInvoiceTotal ? `, ${fmt(p.fapiLastInvoiceTotal)} Kč` : ""}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-brand-50/60 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 text-left font-semibold sm:px-5">Klient</th>
                  <th className="px-2 py-3 text-left font-semibold">Stav</th>
                  <th className="px-2 py-3 text-right font-semibold">Zbývá</th>
                  <th className="hidden px-2 py-3 text-left font-semibold md:table-cell">Trenér</th>
                  <th className="hidden px-2 py-3 text-left font-semibold lg:table-cell">Lokalita</th>
                  <th className="hidden px-2 py-3 text-left font-semibold xl:table-cell">Konec</th>
                  <th className="hidden px-2 py-3 text-left font-semibold md:table-cell">Poslední trénink</th>
                  <th className="hidden px-2 py-3 text-left font-semibold lg:table-cell">Poslední faktura</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  const zbyva = parseInt(p.stavTreninku, 10);
                  const zbyvaDisplay = isNaN(zbyva) ? "—" : zbyva;
                  return (
                    <tr key={p.klient + i}
                      onClick={() => setSelected(p)}
                      onMouseEnter={() => prefetchClientData(p.klient)}
                      className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-brand-50/50 ${
                        p.status === "expired" ? "bg-rose-50/30" : p.status === "alert" ? "bg-amber-50/30" : ""
                      }`}>
                      <td className="px-4 py-3 sm:px-5">
                        <div className="font-semibold text-slate-900 demo-sensitive">{p.klient}</div>
                        <div className="text-xs text-slate-500">{p.balicek}</div>
                      </td>
                      <td className="px-2 py-3">
                        <StatusBadge status={p.status} konecPo={p.konecPo} />
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className={`text-base font-bold tabular-nums ${
                          p.status === "expired" ? "text-rose-600" : p.status === "alert" ? "text-amber-600" : "text-slate-900"
                        }`}>{zbyvaDisplay}</span>
                        <span className="text-xs text-slate-400">/10</span>
                      </td>
                      <td className="hidden px-2 py-3 text-slate-700 md:table-cell">{p.trener || "—"}</td>
                      <td className="hidden px-2 py-3 text-xs text-slate-700 lg:table-cell">{lokalitaKratce(p.lokalita)}</td>
                      <td className="hidden whitespace-nowrap px-2 py-3 text-slate-700 xl:table-cell">
                        {p.konec ? (
                          <div>
                            <div>{formatDate(p.konec)}</div>
                            {p.daysUntilEnd !== null && (
                              <div className={`text-xs ${
                                p.konecPo ? "font-semibold text-rose-600" : p.konecBrzy ? "text-amber-600" : "text-slate-400"
                              }`}>
                                {p.konecPo ? `před ${-p.daysUntilEnd} dny` : `za ${p.daysUntilEnd} dní`}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="hidden whitespace-nowrap px-2 py-3 text-slate-700 md:table-cell">
                        {p.posledniTrenink ? (
                          <div>
                            <div>{formatDate(p.posledniTrenink)}</div>
                            {p.posledniTreninkDays !== null && (
                              <div className={`text-xs ${
                                p.posledniTreninkDays > 15 ? "font-semibold text-rose-600" : p.posledniTreninkDays > 7 ? "text-amber-600" : "text-slate-400"
                              }`}>před {p.posledniTreninkDays} dny</div>
                            )}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="hidden whitespace-nowrap px-2 py-3 text-slate-700 lg:table-cell">
                        {p.fapiLastInvoiceDate ? (
                          <div>
                            <div>{formatDate(p.fapiLastInvoiceDate)}</div>
                            {p.fapiLastInvoiceTotal && (
                              <div className="text-xs text-slate-400">{fmt(p.fapiLastInvoiceTotal)} Kč</div>
                            )}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && <ClientDetail client={selected} onClose={() => setSelected(null)} />}
    </Stranka>
  );
}
